import React, { useState, useEffect, useRef } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { HeroSection } from '@/components/sections/HeroSection';
import { FeaturesSection } from '@/components/sections/FeaturesSection';
import { GallerySection } from '@/components/sections/GallerySection';
import { PrivacyCallout } from '@/components/sections/PrivacyCallout';
import { FaqSection } from '@/components/sections/FaqSection';
import { Footer } from '@/components/sections/Footer';
import { ThemeToggle } from '@/components/ThemeToggle';
import { PLAY_STORE_URL } from '@/lib/constants';
import { setCanonical } from '@/lib/seo';

function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => {
      const el = barRef.current;
      if (!el) return;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const progress = total > 0 ? (window.scrollY / total) * 100 : 0;
      el.style.width = `${progress}%`;
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return <div ref={barRef} className="fixed top-0 left-0 z-[80] h-0.5 bg-[#6750A4] dark:bg-m3-dark-primary pointer-events-none" />;
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-[60] h-[72px] flex items-center transition-all duration-300 ${
        scrolled
          ? 'bg-[#FEF7FF]/85 dark:bg-m3-dark-surface/85 border-b border-[#6750A4]/10 dark:border-m3-dark-primary/15'
          : 'border-b border-transparent'
      }`}
      style={{ backdropFilter: scrolled ? 'blur(16px)' : 'none' }}
    >
      <div className="max-w-[1200px] w-full mx-auto px-8 flex items-center justify-between gap-4">
        <div
          className="text-xl font-bold tracking-[-0.03em] text-[#6750A4] dark:text-m3-dark-primary px-4 py-1.5 rounded-full bg-[#FEF7FF]/70 dark:bg-m3-dark-surface/70 border border-[#6750A4]/15 dark:border-m3-dark-primary/15 backdrop-blur-xl"
        >
          AwayFX
        </div>
        <div className="flex items-center gap-2">
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get AwayFX on Google Play"
            className="hidden sm:block text-[13px] font-medium text-[#6750A4] dark:text-m3-dark-primary px-4 py-1.5 rounded-full bg-[#FEF7FF]/70 dark:bg-m3-dark-surface/70 border border-[#6750A4]/15 dark:border-m3-dark-primary/15 backdrop-blur-xl transition-colors hover:bg-[#EADDFF]/80 dark:hover:bg-[#4F378B]/60"
          >
            Android · Available now
          </a>
          <ThemeToggle className="bg-[#FEF7FF]/70 dark:bg-m3-dark-surface/70 text-[#6750A4] dark:text-m3-dark-primary border border-[#6750A4]/15 dark:border-m3-dark-primary/15 rounded-full backdrop-blur-xl hover:bg-[#EADDFF]/80 dark:hover:bg-[#4F378B]/60" />
        </div>
      </div>
    </nav>
  );
}
export function HomePage() {
  useEffect(() => {
    setCanonical('/');
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-[#FEF7FF] dark:bg-m3-dark-surface text-foreground scroll-smooth">
      <ScrollProgress />
      <Nav />
      <main className="flex-grow">
        <HeroSection />
        <FeaturesSection />
        <GallerySection />
        <PrivacyCallout />
        <FaqSection />
      </main>
      <Footer />
      <Toaster richColors closeButton position="bottom-center" />
    </div>
  );
}
